<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

        <!-- <label class="spl-inline-form mb-1" :for="componentId">
            {{label}}
            <base-popover-icon
                v-if="popoverContent"
                :title="label"
                :content="popoverContent"
                class="ml-2"
            ></base-popover-icon>
        </label> -->

        <b-form-group
            :id="'fieldset-' + componentId"
            class="hot-edit-input-group hot-edit-multiselect mb-0"
            :class="{
                'edit-mode': localEditMode,
                'display-mode': !localEditMode,
                'spl-inputs-loading': loading}"
            :label="label"
            :label-size="size"
            :label-for="'input-' + componentId"
            :label-class="['hot-edit-label', labelVertical ? 'mb-1' : 'mb-0']"
            :label-cols="labelVertical ? null : labelCols"
            :label-align="labelVertical ? 'left' : labelAlign"
            :state="localEditMode ? localState : null"
        >
            <template #label>
                {{ label }}
                <span v-if="required && localEditMode" class="ml-1 text-danger">*</span> 
                <base-popover-icon
                    v-if="popoverContent && localEditMode"
                    class="ml-2"
                    :title="label"
                    :content="popoverContent"    
                ></base-popover-icon>
            </template>

            <div
                v-if="!localEditMode"
                key="noedit"
                class="mb-0 font-size-100"
                :class="[noEditClass, {'ml-3': !labelVertical}]"
            >
                <div v-if="!loading && (selectedVendorName || emptyText)" :class="!!selectedVendorName ? '' : 'text-muted'">
                    {{ selectedVendorName || emptyText }}
                </div>
            </div>

            <multiselect 
                v-if="localEditMode"
                :value="selectedVendor"
                :disabled="loadingPreselectedVendorData"
                class="multiselect-base-vendor-search-select"
                :class="{'invalid': localState === false}"
                :id="componentId" 
                ref="multiselectComponent"
                label="name" 
                track-by="id" 
                tag-placeholder="Press enter"
                :tag-position="newVendorBottom ? 'bottom' : 'top'"
                :placeholder="placeholder"
                select-label="Press enter"
                :show-labels="showListLabels"
                :options="vendorList" 
                :multiple="false" 
                :taggable="!notTaggable"
                :loading="localLoading" 
                :internal-search="false" 
                :preserve-search="true"
                :close-on-select="true" 
                :limit="5"
                :limit-text="limitText" 
                :options-limit="100"
                :max-height="300" 
                :show-no-results="false" 
                :show-no-options="false"
                :hide-selected="false"
                @tag="addVendor"
                @input="updateSelectedVendor"
                @search-change="searchVendors"
                @close="onDropdownClose"
            >
                <template slot="singleLabel" slot-scope="{ option }">
                    <div class="d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center"> <!-- Start Area -->
                            <div>{{ option.name }}</div>
                            <b-badge v-if="option.isNew" variant="success" class="mx-2">NEW</b-badge>
                            <b-badge v-else-if="option.id && !notTaggable" variant="soft-secondary" class="mx-2">{{existingVendorBadge}}</b-badge>
                        </div>
                        <div>  <!-- End Area -->
                            <b-badge variant="soft-primary" @mousedown.prevent.stop="removeSelection" style="cursor: pointer;">X</b-badge>
                            <!-- <b-icon icon="x-square" @mousedown.prevent.stop="removeSelection" style="cursor: pointer;"></b-icon> -->
                        </div>
                        
                    </div>
                </template>

                <template slot="option" slot-scope="props">
                    <div>
                        <div v-if="props.option.id" key="savedVendorBadge">
                            {{ props.option.name }}
                            <b-badge v-if="!notTaggable" variant="soft-secondary" class="ml-2">{{existingVendorBadge}}</b-badge>
                        </div>
                        <div v-else key="newVendorBadge">
                            {{ props.search }}
                            <b-badge variant="success" class="ml-2">{{newVendorBadge}}</b-badge>
                        </div>
                    </div>
                </template>

                <!-- <template slot="clear" slot-scope="props">
                    <div class="multiselect__clear" v-if="selectedVendor" @mousedown.prevent.stop="clearAll(props.search)"></div>
                </template> -->

                <!-- <template slot="caret">
                    <span>&nbsp;</span>
                </template> -->

                <span slot="noOptions">
                    Start typing to search...
                </span>

                <span slot="noResult">
                    Oops! No vendors found. Consider changing the search query.
                </span>

            </multiselect>
        
        </b-form-group>

    </div>
</template>

<script>
// this is used as a type-ahead search for vendors 
// should be used only by INTERNAL people
// looks up vendors by NAME
// NO V-MODEL - can pass in pre-selected vendor in preselected-vendor="{vendorObject}" and listen for updates on 'input' event (maybe should be change event instead?)
// parent component should handle creating new vendor -- this does not create new vendor

import Multiselect from 'vue-multiselect';
import { suppliosApi } from '../js/api-service';
import kebabCase from 'lodash/kebabCase';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';

export default {
    name: 'BaseVendorSearchSelect',
    components: {
        Multiselect,
    },
    props: {
        label: {
            type: String,
            default: 'Company Name',
        },
        preselectedVendor: {  // if you want to pre-select a vendor, pass in full object here
            // but it is NOT a v-model
            // should be an Object with vendor details
            type: Object,
            required: false,
        },
        existingVendorBadge: {  // text to put in the badge for existing vendors
            type: String,
            default: 'SAVED',
        },
        newVendorBadge: {
            type: String,
            default: '+ ADD NEW',
        },
        newVendorBottom: {  // put new vendor on bottom of list?  if not set, goes on top
            type: Boolean,
            default: false,
        },
        showListLabels: {  // show labels in the list like "Press enter to select"
            type: Boolean,
            default: false,
        },
        popoverContent: {
            type: String,
            required: false,
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        noEdit: {  // if true, then this never goes into edit mode
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        labelVertical: {  // if set, it overrides any labelCols and sets labelAlign to 'left'
            type: Boolean,
            default: false,
        },
        labelCols: {  // can set to null to make verical layout! ?  maybe... but you can also set to "auto"
            type: [Number, String],
            default: 3,
        },
        labelAlign: {
            type: String,
            default: 'right'
        },
        size: {
            type: String,
            default: 'md'
        },
        placeholder: {  // placeholder text used inside the multiselect when in edit mode only
            type: String,
            default: "Start typing company name...",
        },
        emptyText: {  // text to show in the value area if theres no value and NOT in edit mode
            type: String,
            required: false,
        },
        loading: { // optional...
            type: Boolean,
            default: false,
        },
        noEditClass: {  // classes (as a string) to apply to value when NOT in edit mode
            type: String,
            required: false,
        },
        notTaggable: {  // sets taggable to false on multiselect, which does not allow adding new, also will hide 'saved' badge
            type: Boolean,
            default: false,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            localLoading: false, 
            vendorList: [],  // search results from API - list of objects
            selectedVendor: null,  // vendor we select goes here, set to this.value to get value from prop if its passed in, then updated locally after that
            loadingPreselectedVendorData: false,  // loading flag for getPreselectedVendorData, used to disable multiselect when this is happening so user input doesn't interfere with setting selectedVendor
            localState: null,  // set this on checkValidity()
        }
    },
    computed: {
        componentId: function() {
            return 'vendor-search-select-' + kebabCase(this.label);
        },
        localEditMode: function() {
            // allows us to override editMode prop if noEdit is set
            if (this.noEdit === true) {
                return false;   // never edit if noEdit === true
            } else {
                return this.editMode;
            }
        },
        hasSelectedVendor: function() {
            if (!this.selectedVendor || isEmpty(this.selectedVendor)) return false;
            return true;
        },
        hasPreselectedVendor: function() {
            if (!this.preselectedVendor || isEmpty(this.preselectedVendor)) return false;
            return true;
        },
        selectedVendorName: function() {
            // get vendor name for display in non-edit mode, or other things...
            // if we're in localEditMode, look at selectedVendor only
            // if we are NOT in localEditMode, look at selectedVendor first, then look at preselectedVendor, maybe it was passed in there and we don't want to deal with copying preselectedVendor to selectedVendor if not in edit mode
            if (this.localEditMode) {
                return ('name' in this.selectedVendor) ? this.selectedVendor.name : '';
            } else {
                if (this.hasSelectedVendor && 'name' in this.selectedVendor) return this.selectedVendor.name;
                if (this.hasPreselectedVendor && 'name' in this.preselectedVendor) return this.preselectedVendor.name;
                return '';
            }
        },
    },
    watch: {
        localEditMode: function(newVal) {
            if (newVal === true) {  // if entering localEditMode
                this.handlePreselectedVendor();  // should fetch vendor?
            }
        },
    },
    created: function() {
        // setup some stuff, like debounced search method
        const debounceTime = 300;
        this.debouncedSearch = debounce(this.performSearch, debounceTime, {'leading': true, 'trailing': true});
        // UPDATE Nov 5 2021 -- this below does not actually check if you have real preselectedVendor -- doesn't work how you expect!
        // ALSO -- you may not have the data yet (passed from prop) in created/mounted -- this likely got created before you have data!
        /* if (this.preselectedVendor) {
            console.log('from BASE VENDOR SEARCH SELECT - we DO have this.preselectedVendor in created');
            console.log(this.preselectedVendor);
            this.handlePreselectedVendor();
        } else {
            console.log('from BASE VENDOR SEARCH SELECT - we DO NOT have this.preselectedVendor in created');
        } */
    },
    mounted() {
        const hasPreselect = !!this.preselectedVendor?.id;
        if (hasPreselect) {
            console.log('from BASE VENDOR SEARCH SELECT - we DO have this.preselectedVendor in mounted');
            console.log(this.preselectedVendor);
            this.handlePreselectedVendor();
        } else {
            console.log('from BASE VENDOR SEARCH SELECT - we DO NOT have this.preselectedVendor in mounted');
        }
    },
    methods: {
        resetValidity: function() {
            // call this from parent to reset localState (to null) so any invalid markup is not showing (like red outline)
            // you will need to call checkValidity() again to check it after this.
            this.localState = null;
        },
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        handlePreselectedVendor: function() {
            // handle object passed into preselectedVendor prop
            // should ONLY be called during created/mounted, or when changing localEditMode... NOT during reactive operation on local edits
            // NEW Nov 5 2021 -- also should be called if this component got mounted before preselectedVendor prop was set, even if not going into edit mode, still need to update the local selectedValue based on new preselectedVendor object
            // TODO - as of Nov 5 2021 -- should make this actually work like a real v-model, pass things down from parent in props, pass updates back up, true 2-way binding
            // during reactive operation all changes happen locally then get reported up... nothing comes back down from parent
            // check if its a valid object with the right keys
            // if edit mode, have to hit API to get options to find this, then set on selected vendor
            // if not edit mode, set it as selectedVendor?
            console.log('hello from handlePreselectedVendor');
            /*  removed this because we still need to handle pre-selected vendor if we're in read-only mode
            if (!this.localEditMode) {
                // non-edit case... likely not used since this component will likely be hidden on forms if not in edit mode
                // for now, don't do anything here... this.selectedVendorName will look at selectedVendor, then at preselectedVendor if we're not in edit mode, so that's enough for now
                console.log('From handlePreselectedVendor - not in localEditMode, so not doing anything');
                return;
            }
            */
            // now handle case where we ARE in localEditMode
            // if preselectedVendor obj has enough info (name and ID) then:
                // 1) push it (set it/copy it) to BOTH selectedVendor and to options array
            // if preselectedVendor obj does NOT have enough info (ie: id only) then fetch from API and push / set to both selectedVendor and options array
            if (this.hasPreselectedVendor && 'id' in this.preselectedVendor && this.preselectedVendor.id) {
                // ok, now we know we at least have ID that isn't blank (but might be junk, like pseudo ID we created somewhere)
                if ('name' in this.preselectedVendor && this.preselectedVendor.name) {
                    // now we know we have a name too... so no need for an API call, just push to options array and set to selectedVendor
                    this.setPreselectedToSelected(this.preselectedVendor);  // this method handles clone/push/set as selectedVendor
                    console.log('From handlePreselectedVendor - just passed preselectedVendor through to selectedVendor, no API call');
                    return;
                } else {
                    // now handle case where we only specified vendor ID in preselectedVendor - hit API
                    this.getPreselectedVendorData(this.preselectedVendor.id); // this fetches data and sets all the things locally
                    console.log('From handlePreselectedVendor - hitting API to get preselectedVendor data');
                    return;
                }
            } else {
                // this is case where we have preselectedVendor, but no real info in it... so we don't REALLY have preselected vendor
                // so don't do anything
                console.log('From handlePreselectedVendor - preselectedVendor did not have any real data, so not doing anything');
                return;
            }
        },
        getPreselectedVendorData: function(companyId) {
            // fetches data for one vendor from API -- called by handlePreselectedVendor
            // called if we only passed in companyId for preselectedVendor, this gets all data for vendor and sets it on selectedVendor and options array
            this.loadingPreselectedVendorData = true;
            suppliosApi.vendorStandard.retrieve(companyId)
            .then(response => {
                console.log('got data for preselected vendor from api');
                console.log(response);
                let preselectedVendorData = response.data;
                if (preselectedVendorData && 'id' in preselectedVendorData && 'name' in preselectedVendorData) {
                    // this is our attempt at validation
                    this.setPreselectedToSelected(preselectedVendorData);
                }
            })
            .catch(error => this.handleError(error, 'Error while getting vendor information'))
            .finally(() => this.loadingPreselectedVendorData = false)
        },
        setPreselectedToSelected: function(preselectedVendorObj) {
            // takes a (most likely) valid preselectedVendor object and sets it as selectedVendor AND pushes it into our options array
            // used in 2 places, so making it a separate function
            let preselectedCopy = cloneDeep(preselectedVendorObj);  // clone so we don't mutate 
            this.vendorList.push(preselectedCopy);  // push to options array
            this.selectedVendor = preselectedCopy;  // set on selectedVendor
            return;
        },
        limitText: function(count) {
            return `and ${count} other vendors`;
        },
        searchVendors: function(searchQuery) {
            // some parameters to modify search
            const minimumChars = 3;  // minimum characters before we call the API
            //const debounceTime = 3000;  // debounce time in ms
            //let debouncedSearch = debounce(this.performSearch, debounceTime, {'leading': true, 'trailing': false});
            if (searchQuery.length < minimumChars) return;
            console.log('hello from inside searchVendors, after minChar block before debouncedSearch');
            this.debouncedSearch(searchQuery);
        },
        //debouncedSearch: debounce(this.performSearch, 3000, {'leading': true, 'trailing': false}),
        performSearch: function(searchQuery) {
            // this gets wrapped in throttle or debounce... don't call it directly - use searchVendors()
            this.localLoading = true;
            suppliosApi.internal.vendorSearch(searchQuery)
            .then(response => {
                console.log('got vendors from search, response below');
                console.log(response);
                this.vendorList = response.data;
            })
            .catch(error => this.handleError(error, 'Error while searching for Vendors'))
            .finally(() => this.localLoading = false)
        },
        addVendor: function(newVendor) {
            // take newVendor (string entered by user) and make new object, 
            // push new object to options array AND to selected value
            // emit 'new-vendor' event, then ALSO emit 'input' event using updateSelectedVendor method
            // let the parent add it to backend
            // parent may only want to listen to 'input' or only to 'new-vendor' -- depends on situation
            // be careful listening to both events... because if user sets a new-vendor, then changes to existing vendor, there's no way to know that if you only watch new-vendor event... so just watching one 'input' event is better in that way
            let tempId = 'new-' + kebabCase(newVendor);
            const newVendorObj = {
                name: newVendor.trim(),
                id: tempId, // needed because options array needs this.  parent will have to know what to do with this
                isNew: true,  // put this in so we know how to look for new objects in input event
            };
            console.log(newVendorObj);
            this.vendorList.push({...newVendorObj});
            this.$emit('new-vendor', {...newVendorObj});  // emit event just for new vendors
            this.updateSelectedVendor(newVendorObj);  // this will emit 'input' event to parent
            // do something here to add a new Vendor (but don't create via API... just set it and pass up to parent without ID)
        },
        updateSelectedVendor: function(selectedVendorObj) {
            // called from @input on multiselect AND from the addVendor method for adding a new vendor
            // set the selectedVendor locally, then emit vendor object to parent
            let newSelection;
            // first, check if we have a real vendor object, if not, then set selectedVendor to null and return
            if (!selectedVendorObj || isEmpty(selectedVendorObj)) {
                newSelection = null;
            } else {
                newSelection = selectedVendorObj;
            }
            this.selectedVendor = newSelection;
            this.$emit('input', cloneDeep(newSelection));
            // and finally reset the vendorList, otherwise it will only be a partial list, will be deceiving to user
            // do it inside a nextTick so any updates to selectedVendor will be complete
            this.$nextTick(() => {
                let keepSelected = true;
                this.resetVendorList(keepSelected);
            });
            
        },
        removeSelection: function() {
            this.updateSelectedVendor(null);
            // don't need these... they'll get called in updateSelectedVendor, and selectedVendor will be null already
            //let keepSelected = false;
            //this.resetVendorList(keepSelected);
        },
        resetVendorList: function(keepSelected = false) {
            // resets our vendor list, EXCEPT FOR THE ONE SELECTED VENDOR -- show that one in the list so it can be removed easily if needed?
            // this is good because if user clicks on dropdown, it will only show a list of results from the last search, which will be a very partial list, will be confusing
            if (keepSelected && this.hasSelectedVendor) {
                this.vendorList = [{...this.selectedVendor}];
            } else {
                this.vendorList = [];
            }
            // and also reset the search string inside the multiselect component
            this.$refs.multiselectComponent.search = '';
        },
        onDropdownClose: function() {
            // if we didn't select anything, then we should clear the list of all options so search remnants don't remain
            let keepSelected = true;
            this.resetVendorList(keepSelected);
            // also checkValidity() on each close, just like we check other inputs on blur
            this.checkValidity();
        },
        checkValidity: function() {
            // return true/false if its valid or not, depends on required prop also
            // also set this.localState while you're at it
            console.log('hello from check validity in multiselect');
            let validity = true;
            if (this.required) {
                validity = this.hasSelectedVendor;
            }
            this.localState = validity ? null : validity;  // this only returns null or false, used to change styling if not valid
            return validity;
        },
        checkInvalidMessage: function() {
            const errorMessage = this.label + ': ' + 'Please select a vendor';
            let valid = this.checkValidity();
            return valid ? '' : errorMessage;
        },
    }
}
</script>

<style>
.multiselect-base-vendor-search-select div.multiselect__tags {
    padding-right: 0.5rem !important;
}

.multiselect-base-vendor-search-select.invalid div.multiselect__tags {
    border-color: var(--danger);
}
.multiselect-base-vendor-search-select .multiselect__content-wrapper {
    box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.2);
}

</style>